import React, { useEffect, useState } from 'react';
import { Card, Form, Input, Button, Space, Tabs, Select, Switch, Divider, Typography, message, Skeleton } from 'antd';
import { PlusOutlined, DeleteOutlined, SaveOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formsApi } from '../../api/forms';
import type { FormDto, FormFieldDto } from '../../api/forms';

const { Option } = Select;

const APP_LANGUAGES = ['de', 'en', 'tr', 'ru', 'ar'];

export const FormBuilder: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [form] = Form.useForm();
    const [fields, setFields] = useState<FormFieldDto[]>([]);

    const { data: formData, isLoading } = useQuery({
        queryKey: ['adminForm', id],
        queryFn: async () => {
            const result = await formsApi.getAdminFormById(id!);
            return result.data as FormDto;
        },
        enabled: !!id
    });

    useEffect(() => {
        if (formData) {
            setFields(formData.fields || []);
            
            const initialValues: any = {
                name: formData.name,
                slug: formData.slug,
                isActive: formData.isActive
            };

            formData.translations?.forEach(t => {
                initialValues[`trans_${t.languageCode}_title`] = t.title;
                initialValues[`trans_${t.languageCode}_description`] = t.description;
                initialValues[`trans_${t.languageCode}_successMessage`] = t.successMessage;
                initialValues[`trans_${t.languageCode}_submitButtonText`] = t.submitButtonText;
            });

            form.setFieldsValue(initialValues);
        }
    }, [formData, form]);

    const saveMutation = useMutation({
        mutationFn: (data: Partial<FormDto>) => formsApi.updateForm(id!, data),
        onSuccess: () => {
            message.success('Form structure saved successfully');
            queryClient.invalidateQueries({ queryKey: ['adminForm', id] });
        },
        onError: () => message.error('Failed to save form structure')
    });

    const addField = () => {
        const newField: FormFieldDto = {
            name: `field_${fields.length + 1}`,
            type: 'text',
            isRequired: false,
            orderIndex: fields.length,
            translations: APP_LANGUAGES.map(lang => ({ languageCode: lang, label: '' }))
        };
        setFields([...fields, newField]);
    };

    const removeField = (index: number) => {
        const newFields = [...fields];
        newFields.splice(index, 1);
        setFields(newFields);
    };

    const updateField = (index: number, updates: Partial<FormFieldDto>) => {
        const newFields = [...fields];
        newFields[index] = { ...newFields[index], ...updates };
        setFields(newFields);
    };

    const updateFieldTranslation = (fieldIndex: number, langCode: string, updates: any) => {
        const newFields = [...fields];
        const transIndex = newFields[fieldIndex].translations.findIndex(t => t.languageCode === langCode);
        
        if (transIndex > -1) {
            newFields[fieldIndex].translations[transIndex] = { ...newFields[fieldIndex].translations[transIndex], ...updates };
        } else {
            newFields[fieldIndex].translations.push({ languageCode: langCode, label: '', ...updates });
        }
        
        setFields(newFields);
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            
            const translations = APP_LANGUAGES.map(lang => ({
                languageCode: lang,
                title: values[`trans_${lang}_title`],
                description: values[`trans_${lang}_description`],
                successMessage: values[`trans_${lang}_successMessage`],
                submitButtonText: values[`trans_${lang}_submitButtonText`]
            })).filter(t => t.title);

            const payload: Partial<FormDto> = {
                id,
                name: values.name,
                slug: values.slug,
                isActive: values.isActive,
                translations,
                fields: fields.map((f, i) => ({ ...f, orderIndex: i }))
            };

            saveMutation.mutate(payload);
        } catch (error) {
            console.error('Validation failed:', error);
        }
    };

    if (isLoading) return <Skeleton active />;

    return (
        <Space orientation="vertical" style={{ width: '100%' }} size="large">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/forms')}>Back to List</Button>
                    <Typography.Title level={4} style={{ margin: 0 }}>Form Builder: {formData?.name}</Typography.Title>
                </Space>
                <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saveMutation.isPending}>
                    Save Structure
                </Button>
            </div>

            <Form form={form} layout="vertical">
                <Card title="Base Info & Translations">
                    <Tabs type="card" items={APP_LANGUAGES.map(lang => ({
                        key: lang,
                        label: lang.toUpperCase(),
                        children: (
                            <Space orientation="vertical" style={{ width: '100%' }}>
                                <Form.Item name={`trans_${lang}_title`} label="Form Title" rules={[{ required: lang === 'de' }]}>
                                    <Input placeholder="Registration Form" />
                                </Form.Item>
                                <Form.Item name={`trans_${lang}_description`} label="Description">
                                    <Input.TextArea placeholder="Please fill out this form to register." />
                                </Form.Item>
                                <Form.Item name={`trans_${lang}_successMessage`} label="Success Message">
                                    <Input placeholder="Thank you for your submission!" />
                                </Form.Item>
                                <Form.Item name={`trans_${lang}_submitButtonText`} label="Submit Button Text">
                                    <Input placeholder="Submit" />
                                </Form.Item>
                            </Space>
                        )
                    }))} />
                </Card>

                <Divider>Fields</Divider>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {fields.map((field, index) => (
                        <Card 
                            key={index}
                            size="small"
                            title={`Field #${index + 1}: ${field.name}`}
                            extra={<Button danger icon={<DeleteOutlined />} onClick={() => removeField(index)} />}
                        >
                            <Space orientation="vertical" style={{ width: '100%' }} size="middle">
                                    <Space size="large" align="start">
                                        <Form.Item label="Field ID (system name)" style={{ width: 200 }}>
                                            <Input 
                                                value={field.name} 
                                                onChange={e => updateField(index, { name: e.target.value })} 
                                                placeholder="e.g. first_name"
                                            />
                                        </Form.Item>
                                        <Form.Item label="Type" style={{ width: 150 }}>
                                            <Select value={field.type} onChange={val => updateField(index, { type: val })}>
                                                <Option value="text">Text</Option>
                                                <Option value="email">Email</Option>
                                                <Option value="number">Number</Option>
                                                <Option value="date">Date</Option>
                                                <Option value="textarea">Textarea</Option>
                                                <Option value="select">Select</Option>
                                                <Option value="checkbox">Checkbox</Option>
                                            </Select>
                                        </Form.Item>
                                        <Form.Item label="Required" valuePropName="checked">
                                            <Switch checked={field.isRequired} onChange={val => updateField(index, { isRequired: val })} />
                                        </Form.Item>
                                        {field.type === 'select' && (
                                            <Form.Item label="Options (comma separated)" style={{ width: 300 }}>
                                                <Input 
                                                    value={field.options} 
                                                    onChange={e => updateField(index, { options: e.target.value })} 
                                                    placeholder="Option 1, Option 2, Option 3"
                                                />
                                            </Form.Item>
                                        )}
                                    </Space>

                                    <Tabs size="small" items={APP_LANGUAGES.map(lang => {
                                        const t = field.translations.find(tr => tr.languageCode === lang);
                                        return {
                                            key: lang,
                                            label: lang.toUpperCase(),
                                            children: (
                                                <Space>
                                                    <Input 
                                                        placeholder="Label" 
                                                        value={t?.label} 
                                                        onChange={e => updateFieldTranslation(index, lang, { label: e.target.value })} 
                                                        style={{ width: 200 }}
                                                    />
                                                    <Input 
                                                        placeholder="Placeholder" 
                                                        value={t?.placeholder} 
                                                        onChange={e => updateFieldTranslation(index, lang, { placeholder: e.target.value })} 
                                                        style={{ width: 200 }}
                                                    />
                                                </Space>
                                            )
                                        };
                                    })} />
                                </Space>
                            </Card>
                    ))}
                </div>

                <Button type="dashed" block icon={<PlusOutlined />} onClick={addField} style={{ marginTop: 16 }}>
                    Add Field
                </Button>
            </Form>
        </Space>
    );
};
